import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/shared/lib/utils';

interface HomeSectionProps {
  title: string;
  /**
   * A linha abaixo do título, e ela **sempre diz de quem são os números**.
   *
   * Obrigatória de propósito. Todo bloco desta tela mostra contagem recortada
   * pela RLS, menos o de densidade, que mostra a filial inteira — dois escopos
   * na mesma página. São legítimos; o que não é legítimo é a pessoa ter de
   * adivinhar qual bloco fala de qual. Ver `messages.home`.
   */
  caption: string;
  action?: { label: string; to: string };
  children: ReactNode;
  className?: string;
}

/**
 * A moldura de um bloco da Início: título, a frase que diz o escopo, e o
 * atalho para a tela onde aquele assunto é tratado a sério.
 *
 * O bloco é um card e não uma seção solta porque a Início passou a ter seis
 * assuntos numa rolagem: sem a borda, as legendas de um bloco encostam nos
 * números do vizinho e o olho junta o que não é para juntar.
 */
export function HomeSection({ title, caption, action, children, className }: HomeSectionProps) {
  return (
    <section className={cn('rounded-lg border border-border bg-card p-4 sm:p-5', className)}>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{caption}</p>
        </div>
        {action ? (
          <Link
            to={action.to}
            className="group flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <span className="hidden sm:inline">{action.label}</span>
            <ArrowRight
              aria-hidden="true"
              className="size-4 transition-transform duration-fast ease-out group-hover:translate-x-0.5"
            />
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}
